<template>
  <div class="tenancyAccountingOrders">
    <InvoiceTrailModal
      v-if="isInvoiceDetailsModalShown"
      :orderId="currentPaymentId"
      :landlordId="landlordId"
      :tenancyId="tenancyId"
      :initialPage="invoiceDetailsModalInitialPage"
      :is-actions-tab-visible="!isSubscriptionFree"
      @newDirectPayment="$emit('fetch-orders', false)"
    />

    <EditInvoiceModal
      v-if="isEditInvoiceModalShown"
      :orderId="currentPaymentId"
      :landlordId="landlordId"
      :tenancyId="tenancyId"
      @order-saved="$emit('fetch-orders', false)"
    />

    <CreditNoteModal
      v-if="isCreditNoteModalShown"
      :landlordId="landlordId"
      :tenancyId="tenancyId"
      :orderId="currentPaymentId"
      :paymentId="currentPaymentId"
      @newCreditNote="$emit('fetch-orders', false)"
    />

    <hl-Modal
      v-if="showEmitInvoiceModal"
      iconClass="budicon-broadcast text-secondary"
      :title="invoiceEmitted ? 'Faktura er utstedt' : 'Utsted faktura'"
      class="text-center"
    >
      <div v-if="invoiceEmitted">
        <p>Faktura er sendt leietaker og vil bli synlig i fakturalisten innen kort tid.</p>

        <div class="mt-5">
          <button class="btn btn-outline-secondary ml-2" @click="showEmitInvoiceModal = false">Lukk</button>
        </div>
      </div>

      <div v-else>
        <p>
          Denne fakturaen vil automatisk bli sendt til leietaker 10 dager før forfallsdato. Om du heller ønsker å sende
          den nå klikker du på “Bekreft”.
        </p>

        <div class="mt-5">
          <button class="btn btn-secondary mr-2" @click="emitInvoice">Bekreft</button>
          <button class="btn btn-outline-secondary ml-2" @click="showEmitInvoiceModal = false">Avbryt</button>
        </div>
      </div>
    </hl-Modal>

    <hl-Modal
      v-if="showCancelInvoiceModal"
      iconClass="budicon-alert-sign text-danger"
      :title="invoiceCancelled ? 'Faktura er kansellert' : 'Kanseller faktura'"
      class="text-center"
    >
      <div v-if="invoiceCancelled">
        <p>Faktura er kansellert, og sendes derfor ikke til leietaker. Den vil vises i fakturalisten som kansellert.</p>

        <div class="mt-5">
          <button class="btn btn-outline-secondary ml-2" @click="showCancelInvoiceModal = false">Lukk</button>
        </div>
      </div>
      <div v-else>
        <p>Vennligst bekreft at du ønsker å kansellere (slette) denne fakturaen.</p>

        <div class="mt-5">
          <button class="btn btn-danger mr-2" @click="cancelInvoice">Bekreft</button>
          <button class="btn btn-outline-secondary ml-2" @click="showCancelInvoiceModal = false">Avbryt</button>
        </div>
      </div>
    </hl-Modal>

    <hl-Loader v-if="isFetchingInitialOrders" class="col-12 mt-4 text-center" />

    <List v-else-if="orders.length">
      <ListHeader v-if="!showListsItemsAsCards">
        <div class="col">Periode</div>
        <div class="col">Type</div>
        <div class="col">Forfallsdato</div>
        <div class="col">Beløp</div>
        <div class="col">Saldo</div>
        <div class="col">Status</div>
        <div class="col text-right">Vis/Endre</div>
      </ListHeader>

      <!-- Card View -->
      <div v-for="payment in orders" :key="payment.id" class="position-relative">
        <ListItemCard
          v-if="showListsItemsAsCards"
          :windowWidth="getWindowWidth"
          class="border-status d-inline-block"
          :class="'border-status-' + getBorderStatus(payment.status).colorClass"
        >
          <ActionsMenu
            v-if="getTenancy.subscription.subscriptionPlanName !== 'Garantert' || isUserSudo"
            class="list-item-card-actions"
            :menuItems="menuItems(payment)"
            :class="{
              actionsMenuSudo: getTenancy.subscription.subscriptionPlanName === 'Garantert' && isUserSudo,
            }"
          />
          <div class="text-center mt-2">
            <div>
              <b>
                <h6 v-if="payment.period">{{ payment.period | invoicePeriod }}</h6>
                <h6 v-else>Ukjent</h6>
              </b>
            </div>
            <div class="mt-2">
              <span class="mr-1">{{ paymentTypeMap[payment.typeOf] }}</span>
              |
              <span class="ml-1" :class="'text-' + getPaymentStatus(payment.status).colorClass">{{
                getPaymentStatus(payment.status).value
              }}</span>
            </div>
          </div>

          <div class="row mt-4 mb-2 py-3">
            <div class="col">
              <p class="m-0">
                <b class="text-secondary">Type</b>
              </p>
              {{ typeOfInvoice(payment.typeOf) }}
            </div>

            <div class="col text-secondary d-none d-md-block">
              <p class="m-0">
                <b class="text-secondary">Forfallsdato</b>
              </p>
              <hl-Date :date="payment.date.due" />
            </div>
          </div>

          <div class="row mb-2 py-3">
            <div class="col">
              <p class="m-0">
                <b class="text-secondary">Beløp</b>
              </p>
              <hl-Currency :amount="payment.amount.invoiced" />
            </div>

            <div class="col text-secondary d-none d-md-block">
              <p class="m-0">
                <b class="text-secondary">Saldo</b>
              </p>
              <hl-Currency :amount="payment.amount.unpaid" />
            </div>
          </div>
        </ListItemCard>
        <!-- / Card View -->

        <!-- List view -->
        <ListItem
          v-else
          :class="'border-status-' + getBorderStatus(payment.status).colorClass"
          class="border-status"
        >
          <div class="col text-capitalize">{{ payment.period | invoicePeriod }}</div>
          <div class="col">
            {{ typeOfInvoice(payment.typeOf) }}
          </div>
          <div class="col">
            <hl-Date :date="payment.date.due" />
          </div>
          <div class="col">
            <hl-Currency :amount="payment.amount.invoiced" />
          </div>
          <div class="col">
            <hl-Currency :amount="payment.amount.unpaid" />
          </div>
          <div class="col" :class="'text-' + getPaymentStatus(payment.status).colorClass">
            {{ getPaymentStatus(payment.status).value }}
          </div>
          <div class="col text-right">
            <ActionsMenu
              v-if="getTenancy.subscription.subscriptionPlanName !== 'Garantert' || isUserSudo"
              :menuItems="menuItems(payment)"
              :class="{
                actionsMenuSudo: getTenancy.subscription.subscriptionPlanName === 'Garantert' && isUserSudo,
              }"
              variant="button"
              buttonText="Handlinger"
            />
          </div>
        </ListItem>
      </div>

      <div class="text-center mt-5">
        <hl-Button
          variant="secondary"
          outline
          size="sm"
          @click="$emit('fetch-orders', true)"
        >
          <span v-if="!isOrderListExpanded">
            Vis flere
          </span>

          <span v-else>
            Vis færre
          </span>

          <hl-LoaderDots v-if="isFetchingSubsequentOrders" class="ml-2" />
        </hl-Button>
      </div>
    </List>

    <div v-else class="mt-5 text-center">
      <img src="@/assets/empty-interest-management.svg" width="140" />
      <h5 class="mt-5">Leieforholdet har ingen fakturaer</h5>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapMutations, mapActions } from 'vuex';
import moment from 'moment';
import List from '@/components/UI/list/List.vue';
import ListHeader from '@/components/UI/list/ListHeader.vue';
import ListItem from '@/components/UI/list/ListItem.vue';
import ListItemCard from '@/components/UI/list/ListItemCard';
import ActionsMenu from '@/components/UI/ActionsMenu';
import InvoiceTrailModal from '@/components/tenancy/modals/InvoiceTrailModal';
import EditInvoiceModal from '@/components/tenancy/modals/EditInvoiceModal';
import CreditNoteModal from '@/components/tenancy/modals/CreditNoteModal';
import Date from '@modules/husleie-framework/src/components/time/Date.vue';
import Currency from '@modules/husleie-framework/src/components/currency/Currency.vue';
import checkInvoiceOverdue from '@/domain/checkInvoiceOverdue';
import Modal from '@modules/husleie-framework/src/components/modals/Modal.vue';
import Button from '@modules/husleie-framework/src/components/buttons/Button.vue';
import Loader from '@modules/husleie-framework/src/components/loaders/Loader.vue';
import LoaderDots from '@modules/husleie-framework/src/components/loaders/LoaderDots.vue';

export default Vue.extend({
  name: 'TenancyAccountingOrders',
  data() {
    return {
      currentPaymentId: undefined,
      paymentTypeMap: {
        rentInvoice: 'Husleie',
        otherInvoice: 'Tillegg',
      },
      invoiceDetailsModalInitialPage: 'trail',
      showEmitInvoiceModal: false,
      showCancelInvoiceModal: false,
      invoiceTrailChosenInvoiceAction: undefined,
      invoiceEmitted: false,
      invoiceCancelled: false,
      orderToBeCancelled: undefined,
      orderToBeIssued: undefined,
    };
  },
  props: {
    landlordId: {
      type: Number,
      required: true,
    },
    tenancyId: {
      type: Number,
      required: true,
    },
    isSubscriptionFree: {
      type: Boolean,
      required: false,
    },
    orders: {
      type: Array,
      required: true,
    },
    isFetchingInitialOrders: {
      type: Boolean,
      default: false,
    },
    isOrderListExpanded: {
      type: Boolean,
      default: false,
    },
    isFetchingSubsequentOrders: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    List,
    ListHeader,
    ListItem,
    ListItemCard,
    ActionsMenu,
    InvoiceTrailModal,
    EditInvoiceModal,
    CreditNoteModal,
    'hl-Date': Date,
    'hl-Currency': Currency,
    'hl-Modal': Modal,
    'hl-Button': Button,
    'hl-Loader': Loader,
    'hl-LoaderDots': LoaderDots,
  },
  computed: {
    ...mapGetters(['getWindowWidth', 'isUserSudo']),
    ...mapGetters('tenancy', [
      'getTenancy',
      'isInvoiceDetailsModalShown',
      'isCreditNoteModalShown',
      'isEditInvoiceModalShown',
    ]),

    showListsItemsAsCards() {
      return this.getWindowWidth < 1200;
    },
  },
  methods: {
    ...mapMutations('tenancy', ['toggleCreditNoteModal', 'toggleEditInvoiceModal']),
    ...mapActions('tenancy', [
      'toggleInvoiceDetailsModal',
      'postIssueTenancyAccountingOrder',
      'deleteTenancyAccountingOrder',
    ]),

    toggleInvoiceDetailsActions(bool) {
      this.invoiceDetailsModalInitialPage = 'actions';
      this.toggleInvoiceDetailsModal(bool);
    },

    getBorderStatus(status) {
      const invoiceDueDate = moment().add(status.paidDaysDelta, 'd');
      const defaultClass = { colorClass: 'light' };
      const primary = { colorClass: 'primary' };
      const secondary = { colorClass: 'secondary' };
      const danger = { colorClass: 'danger' };
      const warning = { colorClass: 'warning' };

      if (status.order == null || status.payment == null) return defaultClass;
      if (status.order === 'draft') return secondary;
      if (status.payment === 'paid') return primary;
      if (status.payment === 'unpaid' && checkInvoiceOverdue(invoiceDueDate)) return danger;
      if (status.payment === 'unpaid' && status.paidDaysDelta < 0) return warning;
      return defaultClass;
    },
    futureInvoicesMenuItems(id) { 
      return [
        {
          name: 'Endre',
          action: () => {
            this.currentPaymentId = id;
            this.toggleEditInvoiceModal(true);
          },
        },
        {
          name: 'Utsted',
          action: () => {
            this.orderToBeIssued = id;
            this.invoiceEmitted = false;
            this.showEmitInvoiceModal = true;
          },
        },
        {
          name: 'Kanseller',
          action: () => {
            this.orderToBeCancelled = id;
            this.invoiceCancelled = false;
            this.showCancelInvoiceModal = true;
          },
        },
      ];
    },
    pastInvoicesMenuItems(id) {
      return [
        {
          name: 'Vis',
          action: () => {
            this.currentPaymentId = id;
            this.toggleInvoiceDetailsModal(true);
          },
        },
        {
          name: 'Kanseller',
          action: () => {
            this.currentPaymentId = id;
            this.toggleCreditNoteModal(true);
          },
        },
        {
          name: 'Andre handlinger',
          action: () => {
            this.currentPaymentId = id;
            this.toggleInvoiceDetailsActions(true);
          },
        },
      ];
    },
    getPaymentStatus(status) {
      if (status == null) return;

      const colorClass = 'secondary';
      if (status.order === 'draft') {
        return {
          colorClass,
          value: 'Utkast',
        };
      }

      if (status.order === 'canceled') {
        return {
          colorClass,
          value: 'Kansellert',
        };
      }

      // Handling payments status where status.order === invoiced
      if (status.payment === 'canceled') {
        return {
          colorClass,
          value: 'Kansellert',
        };
      }

      if (status.payment === 'unpaid') {
        return {
          colorClass,
          value: 'Ubetalt',
        };
      }

      if (status.payment === 'paid') {
        return {
          colorClass,
          value: 'Betalt',
        };
      }

      return {
        colorClass,
        value: 'Ukjent',
      };
    },
    typeOfInvoice(invoiceType) {
      return (
        {
          rentInvoice: 'Husleie',
          creditNote: 'Kredittnota',
          otherInvoice: 'Tilleggsfaktura',
        }[invoiceType] || 'Ukjent'
      );
    },
    menuItems(payment) {
      if (this.isSubscriptionFree) {
        return [
          {
            name: 'Vis',
            action: () => {
              this.currentPaymentId = payment.id;
              this.toggleInvoiceDetailsModal(true);
            },
          },
        ];
      }

      if (payment.status.order === 'draft') {
        return this.futureInvoicesMenuItems(payment.id);
      } else {
        return this.pastInvoicesMenuItems(payment.id);
      }
    },
    async emitInvoice() {
      try {
        await this.postIssueTenancyAccountingOrder({
          landlordId: this.landlordId,
          tenancyId: this.tenancyId,
          orderId: this.orderToBeIssued,
        });
        this.$toast('Success', 'success');
        this.invoiceEmitted = true;
        this.$emit('fetch-orders', false);
      } catch (error) {
        this.$toast('Kunne ikke utstede faktura.', 'error');
      }
    },
    async cancelInvoice() {
      try {
        await this.deleteTenancyAccountingOrder({
          landlordId: this.landlordId,
          tenancyId: this.tenancyId,
          orderId: this.orderToBeCancelled,
        });
        this.$toast('Success', 'success');
        this.invoiceCancelled = true;
        this.$emit('fetch-orders', false);
      } catch (error) {
        this.$toast('Kunne ikke kansellere faktura.', 'error');
      }
    },
    handleQueryParameters() {
      const query = this.$route.query;
      
      if (!query.orderDetails) {
        return;
      }

      this.currentPaymentId = query.orderDetails;

      if (query.firstPage === 'actions') {
        this.invoiceDetailsModalInitialPage = 'actions';
      }

      this.toggleInvoiceDetailsModal(true);
    },
  },
  filters: {
    invoicePeriod(value) {
      if (!value) {
        return;
      }

      const regex = /^\d{4}-\d{2}-\d{2}$/;
      const match = regex.exec(value);

      if (!match) {
        return value;
      }

      return moment(match).format('MMMM YYYY');
    },
  },
  async created() {
    this.handleQueryParameters();
  },
});
</script>

<style lang="scss" scoped>
.list-item-card-actions {
  position: absolute;
  top: 1rem;
  right: 1rem;
}

.border-status {
  border-left-width: 5px;
  border-left-style: solid;
}

.border-status-primary {
  border-left-color: $primary;
}

.border-status-danger {
  border-left-color: $danger;
}

.border-status-secondary {
  border-left-color: $secondary;
}

.border-status-warning {
  border-left-color: $warning;
}

.actionsMenuSudo {
  color: #ed2135;
}
</style>
